import React, { useMemo, useState } from 'react';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import { useAdminLeaderboard } from '@/services/gamification';
import { useTheme } from '@/contexts/ThemeContext';
import { phantomProps } from '@/config/phantom';
import CongratulationsTrophyIcon from '@/components/icons/CongratulationsTrophyIcon';
import GradientCard from './GradientCard';
import LeaderboardRow, { LeaderboardEntry } from './LeaderboardRow';

/** Rows shown before the "Show All" expansion. */
const DEFAULT_VISIBLE = 5;
/** One fetch covers the whole board (API caps per_page at 100), so expanding
 *  is a client-side slice — no refetch, no loading flash. */
const FETCH_LIMIT = 100;

const LeaderboardCard: React.FC = () => {
  const { theme } = useTheme();
  const [showAll, setShowAll] = useState(false);

  const { data, isLoading } = useAdminLeaderboard({ page: 1, per_page: FETCH_LIMIT });

  const rows: LeaderboardEntry[] = useMemo(
    () =>
      (data?.rankings ?? []).map((r) => ({
        rank: r.rank,
        membershipId: r.membership_id,
        firstName: r.first_name,
        lastName: r.last_name,
        avatarUrl: r.avatar_url,
        points: r.balance,
      })),
    [data]
  );

  return (
    <GradientCard className="rounded-3xl">
      <header className="flex items-center justify-between px-6 py-5">
        <h2 className="text-[1.4rem] font-medium leading-snug text-primary-500 dark:text-primary-300">Current Leaderboard</h2>
      </header>

      <phantom-ui {...phantomProps(theme === 'dark', isLoading)}>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[480px] border-collapse">
            <thead data-shimmer-ignore>
              <tr className="bg-[rgb(var(--color-border-primary)/0.18)] dark:bg-[rgb(var(--color-text-secondary)/0.08)]">
                <th className="px-6 py-3 text-left body-small font-semibold text-text-secondary w-16">
                  Rank
                </th>
                <th className="px-4 py-3 text-left body-small font-semibold text-text-secondary">
                  Learner
                </th>
                <th className="px-4 py-3 text-right body-small font-semibold text-text-secondary">
                  Points
                </th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <tr key={i} className="border-b border-border-primary/60">
                    <td className="px-6 py-3.5">
                      <div className="h-3.5 w-6 rounded" />
                    </td>
                    <td className="px-4 py-3.5">
                      <div className="flex items-center gap-3">
                        <div className="h-9 w-9 flex-shrink-0 rounded-full" />
                        <div className="h-3.5 w-32 rounded" />
                      </div>
                    </td>
                    <td className="px-4 py-3.5">
                      <div className="ml-auto h-3.5 w-12 rounded" />
                    </td>
                  </tr>
                ))
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={3} className="py-16 text-center">
                    <div className="flex flex-col items-center gap-3 text-text-secondary">
                      <CongratulationsTrophyIcon className="h-10 w-10 opacity-40" color={'black'} />
                      <p className="body-base">No points earned yet</p>
                    </div>
                  </td>
                </tr>
              ) : (
                (showAll ? rows : rows.slice(0, DEFAULT_VISIBLE)).map((entry) => (
                  <LeaderboardRow key={entry.membershipId} entry={entry} />
                ))
              )}
            </tbody>
          </table>
        </div>
      </phantom-ui>

      {!isLoading && rows.length > DEFAULT_VISIBLE && (
        <footer className="flex items-center justify-center px-6 py-4">
          <button
            type="button"
            onClick={() => setShowAll((prev) => !prev)}
            className="focus-ring inline-flex items-center gap-1.5 rounded-full px-2 py-1 body-small font-semibold text-primary-600 transition-colors hover:bg-primary-50 hover:text-primary-700 dark:text-primary-400 dark:hover:bg-primary-900/20 dark:hover:text-primary-300 active:scale-[0.97]"
          >
            {showAll ? 'Show Less' : `Show All (${rows.length})`}
            {showAll ? (
              <ChevronUpIcon className="h-4 w-4" />
            ) : (
              <ChevronDownIcon className="h-4 w-4" />
            )}
          </button>
        </footer>
      )}
    </GradientCard>
  );
};

export default LeaderboardCard;
